import { useNavigate } from 'react-router-dom';
import type { FashionModel } from '@studio/shared';

import { assetUrl } from '../lib/api';
import { useT } from '../lib/i18n';
import { Carousel } from './Carousel';
import { OptionCard } from './OptionCard';

interface Props {
  models: FashionModel[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** One model only: picking another replaces the first. */
  single?: boolean;
}

/** The cast, as cards with their portraits. */
export function ModelChooser({ models, selected, onChange, single = false }: Props) {
  const t = useT();
  const navigate = useNavigate();

  if (models.length === 0) {
    return (
      <div className="empty">
        <p className="muted">{t('shoot.noModels')}</p>
        <button className="btn btn--primary" onClick={() => navigate('/models')}>
          {t('shoot.createModel')}
        </button>
      </div>
    );
  }

  const withoutPortrait = models.filter((m) => selected.includes(m.id) && m.referenceAssetIds.length === 0);

  return (
    <>
      <Carousel count={t('shoot.castOf', { done: selected.length, total: models.length })}>
        {models.map((model) => (
          <OptionCard
            key={model.id}
            title={model.name}
            // Who this is, in the reader's language — not the English line
            // the renderer is sent.
            description={[
              t(`enum.gender.${model.appearance.gender}`),
              model.appearance.ageRange,
              t(`enum.build.${model.appearance.build}`),
            ]
              .filter(Boolean)
              .join(' · ')}
            spec={t(model.referenceAssetIds.length ? 'shoot.portraitOnFile' : 'shoot.noPortrait')}
            selected={selected.includes(model.id)}
            example={undefined}
            fallback={
              model.referenceAssetIds[0] ? (
                <img
                  src={assetUrl(model.referenceAssetIds[0])}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: '1.5rem', color: 'var(--ink-3)' }}>
                  {model.name.slice(0, 1).toUpperCase()}
                </span>
              )
            }
            onToggle={() =>
              onChange(
                selected.includes(model.id)
                  ? selected.filter((id) => id !== model.id)
                  : single
                    ? [model.id]
                    : [...selected, model.id],
              )
            }
          />
        ))}
      </Carousel>
      {withoutPortrait.length > 0 && (
        <div className="banner">
          <span>
            {t('shoot.noPortraitWarning', { names: withoutPortrait.map((m) => m.name).join(', ') })}
          </span>
        </div>
      )}
    </>
  );
}
